import { useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import { getCurrencyText } from '@bingads-webui-react/currency';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AUTO_BIDDING_FEATURE_IDS, FAC_STATUS } from '../constants';

export const useAutoBiddingFac = () => {
  const serverContext = useContext(ServerContext);
  const ccJsData = useJsDataContext();
  const instrumentation = useContext(InstrumentationContext);
  const i18n = useContext(I18nContext);

  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'AutoBiddingCoupon',
    activityName: 'CouponRender',
  }), [instrumentation]);


  const getCoupon = useCallback(
    () =>
      ccJsData.FeatureAdoptionCoupon
        .findAll({ aid: serverContext.CurrentAccount.Id })
        .catch((err) => {
        // It seems this returns 401 sometimes, which might just be user token expiring. Do just trace for now to see what issues are here.
          activity.trace(`Got following response when fetching FACs: ${JSON.stringify(err)}`);
          return [];
        })
        .then(coupons => _.filter(
          coupons,
          ({ Status, FeatureId }) => (FeatureId === AUTO_BIDDING_FEATURE_IDS.MAX_CLICKS || FeatureId === AUTO_BIDDING_FEATURE_IDS.MAX_CONVERSIONS)
          && (Status === FAC_STATUS.NOT_IN_PROGRESS || Status === FAC_STATUS.ACTIVE)
        )).then((coupons) => {
          if (coupons.length > 0) {
            const coupon = coupons[0];
            coupon.CouponUpfrontSpend = 50;

            return coupon;
          }

          return null;
        })
    , [ccJsData.FeatureAdoptionCoupon, serverContext.CurrentAccount.Id, activity]
  );

  const updateCouponStatus = useCallback((coupon, status) => ccJsData.FeatureAdoptionCoupon
    .update(
      coupon.id,
      {
        adoption: {
          ...coupon,
          Status: status,
        },
      }
    ), [ccJsData.FeatureAdoptionCoupon]);

  const activateCoupon = useCallback(coupon => updateCouponStatus(coupon, FAC_STATUS.ACTIVE), [updateCouponStatus]);

  const adoptCoupon = useCallback(coupon => updateCouponStatus(coupon, FAC_STATUS.ADOPTED), [updateCouponStatus]);

  const getFormattedCurrencyValue = useCallback(value =>
    getCurrencyText({
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
      amount: value,
      i18n,
      maxFractionDigits: 0,
      hideCurrencyCode: true,
    }), [i18n, serverContext]);

  return {
    getCoupon,
    activateCoupon,
    adoptCoupon,
    getFormattedCurrencyValue,
  };
};
